import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Check, Info } from "lucide-react";
import logo from "../../assets/logos/black_logo.png";
import { registerPostAffiliateSaleAsync } from "../../utils/postAffiliatePro";
import "./ThankYou3.css";

const ONBOARDING_BASE_URL = "https://vigmd.com/onboarding/";
const ONBOARDING_INTAKE_STORAGE_KEY = "checkout-intake-state";
const WEIGHT_LOSS_CATEGORY_NAME = "Weight Loss";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function monthNumberToFullName(value) {
  const monthIndex = Number.parseInt(value, 10) - 1;
  if (monthIndex < 0 || monthIndex > 11 || Number.isNaN(monthIndex)) {
    return value;
  }
  return MONTH_NAMES[monthIndex];
}

function formatStateForOnboardingQuery(state) {
  return state.trim().toUpperCase();
}

function readStoredIntake() {
  try {
    const raw = localStorage.getItem(ONBOARDING_INTAKE_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function readStoredOrder() {
  try {
    const raw = localStorage.getItem("orderData");
    if (!raw || raw === "null") return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function mergeIntake(fromNav, stored) {
  const s = stored || {};
  return {
    state: fromNav?.state ?? s.state ?? "",
    firstName: fromNav?.firstName ?? s.firstName ?? "",
    lastName: fromNav?.lastName ?? s.lastName ?? "",
    dobMonth: fromNav?.dobMonth ?? s.intakeDobMonth ?? "",
    dobDay: fromNav?.dobDay ?? s.intakeDobDay ?? "",
    dobYear: fromNav?.dobYear ?? s.intakeDobYear ?? "",
    email: fromNav?.email ?? s.email ?? "",
    phone: (fromNav?.phone ?? s.phone ?? "").replace(/\D/g, ""),
    sex: fromNav?.sex ?? s.sex ?? "",
    heightFeet: fromNav?.heightFeet ?? s.heightFiFeet ?? "",
    heightInches: fromNav?.heightInches ?? s.heightFiInches ?? "",
    weightLbs: fromNav?.weightLbs ?? s.weightLbs ?? "",
    goalWeight: fromNav?.goalWeight ?? s.goalWeight ?? "",
  };
}

function buildOnboardingQueryString(intake, orderId) {
  const params = new URLSearchParams();
  const usState = formatStateForOnboardingQuery(intake.state);
  const dobMonthRaw = intake.dobMonth.trim();
  const dobMonth =
    /^\d{1,2}$/.test(dobMonthRaw) && dobMonthRaw.length <= 2
      ? monthNumberToFullName(dobMonthRaw)
      : dobMonthRaw;
  const sexNormalized = intake.sex.trim().toLowerCase();

  params.set("usState", usState);
  params.set("fullName[first]", intake.firstName.trim());
  params.set("fullName[last]", intake.lastName.trim());
  params.set("dob[day]", intake.dobDay.trim());
  params.set("dob[month]", dobMonth);
  params.set("dob[year]", intake.dobYear.trim());
  params.set("email", intake.email.trim());
  params.set("phoneNumber", intake.phone.replace(/\D/g, ""));
  params.set("sex", sexNormalized);
  params.set("height_fi_feet", intake.heightFeet.trim());
  params.set("height_fi_inches", intake.heightInches.trim());
  params.set("weight_lbs", intake.weightLbs.trim());
  params.set("goal_weight", intake.goalWeight.trim());

  const id = orderId != null ? String(orderId).trim() : "";
  if (id) params.set("order_id", id);

  return params.toString();
}

const ThankYou3 = () => {
  const location = useLocation();
  const state = location.state || null;
  const [searchParams] = useSearchParams();
  const [storedIntake, setStoredIntake] = useState(null);
  const [storedOrder, setStoredOrder] = useState(null);

  let memberPortalUrl = "https://member.vigmd.com/"; // Default URL

  useEffect(() => {
    setStoredIntake(readStoredIntake());
    setStoredOrder(readStoredOrder());
  }, []);

  const emailFromNav = state?.email;
  const emailFromQuery = searchParams.get("email") ?? undefined;
  const email =
    emailFromNav ||
    emailFromQuery ||
    storedOrder?.email ||
    storedIntake?.email ||
    "your email";

  const orderIdFromNav = state?.orderId;
  const orderIdFromQuery =
    searchParams.get("orderId") ?? searchParams.get("order_id") ?? undefined;
  const orderId =
    orderIdFromNav != null && String(orderIdFromNav).trim() !== ""
      ? orderIdFromNav
      : orderIdFromQuery != null && String(orderIdFromQuery).trim() !== ""
        ? orderIdFromQuery
        : (storedOrder?.orderId ?? storedIntake?.orderId);

  const orderSuccessful =
    orderId !== undefined &&
    orderId !== null &&
    String(orderId).trim() !== "";

  const orderAmount = state?.amount;
  const currency = state?.currency || "USD";
  const productId = state?.productId;
  const orderIdsFromState = state?.orderIds;
  const productIdsFromState = state?.productIds;
  const productSkuFromState = state?.productSku;
  const productSkusFromState = state?.productSkus;
  const productPricesFromState = state?.productPrices;
  const productTotalFromState = state?.productTotal;

  const questionnaireData = state?.questionnaireData;
  const productCategoryName =
    state?.product_category_name ??
    state?.productCategoryName ??
    storedOrder?.product_category_name ??
    searchParams.get("product_category_name");
  const isWeightLossProduct =
    productCategoryName === WEIGHT_LOSS_CATEGORY_NAME;

  const intake = useMemo(
    () => mergeIntake(questionnaireData, storedIntake),
    [questionnaireData, storedIntake]
  );

  const handleCompleteIntake = () => {
    const qs = buildOnboardingQueryString(intake, orderId);
    window.location.href = `${ONBOARDING_BASE_URL}?${qs}`;
  };

  const handleAccessMemberPortal = () => {
    window.location.href = memberPortalUrl;
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const w = window;

    if (w.sendInflektionEvent && email && orderSuccessful) {
      w.sendInflektionEvent(328, email, { transaction_id: String(orderId) });
    }

    if (
      w.sendInflektionConversion &&
      email &&
      orderSuccessful &&
      orderAmount != null &&
      productId != null
    ) {
      w.sendInflektionConversion({
        amount: orderAmount,
        currency,
        order_id: String(orderId),
        email,
        items: [
          {
            ps: String(productId),
            p: orderAmount,
            qty: 1,
          },
        ],
      });
    }
  }, [email, orderId, orderSuccessful, orderAmount, currency, productId]);

  useEffect(() => {
    if (!orderSuccessful) return;
    (async () => {
      await registerPostAffiliateSaleAsync({
        orderId,
        orderIds: orderIdsFromState,
        productTotal: orderAmount ?? productTotalFromState,
        productId,
        productIds: productIdsFromState,
        productSku: productSkuFromState,
        productSkus: productSkusFromState,
        productPrices: productPricesFromState,
      });
    })();
  }, [
    orderSuccessful,
    orderId,
    orderIdsFromState,
    orderAmount,
    productTotalFromState,
    productId,
    productIdsFromState,
    productSkuFromState,
    productSkusFromState,
    productPricesFromState,
  ]);

  return (
    <div className="thankyou3-page">
      <div className="thankyou3-bg thankyou3-bg--glow" aria-hidden />
      <div className="thankyou3-bg thankyou3-bg--blobs" aria-hidden />

      <div className="thankyou3-card">
        <div className="thankyou3-inner">
          <div className="thankyou3-logo-wrap center">
            <center>
              <img src={logo} alt="" className="thankyou3-logo" />
            </center>
          </div>

          <div className="thankyou3-heading-row">
            <div className="thankyou3-icon-circle">
              <Check className="thankyou3-check" aria-hidden />
            </div>
            <h1 className="thankyou3-title">
              Payment received.
              <br />
              Thanks for your order!
            </h1>
          </div>

          <h2 className="thankyou3-subtitle">You&apos;re almost there!</h2>

          <p className="thankyou3-lead">
            Now let&apos;s get your medication to you. Complete your intake so
            we can process your prescription.
          </p>

          {orderSuccessful ? (
            <>
              {isWeightLossProduct ? (
                <>
                  <div className="thankyou3-callout">
                    <div className="thankyou3-info-icon-wrap">
                      <Info className="thankyou3-info-icon" aria-hidden />
                    </div>
                    <p className="thankyou3-callout-text">
                      <span className="thankyou3-callout-strong">
                        Important:
                      </span>{" "}
                      Complete your medical intake below so we can send your
                      medication.
                    </p>
                  </div>

                  <button
                    type="button"
                    className="thankyou3-cta"
                    onClick={handleCompleteIntake}
                  >
                    Complete Your Intake
                  </button>

                  <p className="thankyou3-footnote">
                    Most people finish in under 3 minutes.
                  </p>
                </>
              ) : (
                <button
                  type="button"
                  className="thankyou3-cta"
                  onClick={handleAccessMemberPortal}
                >
                  Access Member Portal
                </button>
              )}
            </>
          ) : (
            <p className="thankyou3-muted thankyou3-muted--block">
              If you just completed checkout, open this page from your checkout
              confirmation. Otherwise, check your email for next steps.
            </p>
          )}

          <div className="thankyou3-footer">
            Questions?{" "}
            <a href="mailto:support@vigmd.com" className="thankyou3-link">
              support@vigmd.com
            </a>
            <p className="thankyou3-footer-note">
              We respond during business hours, M—F.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThankYou3;
